/**
 * KuendigungsAssistent.jsx – Phase 1A
 *
 * Neuer Kündigungsassistent PARALLEL zur bestehenden KuendigungModal.jsx.
 * Die bestehende Kündigung bleibt vollständig unverändert.
 *
 * Phase 1A enthält:
 * - 3 Kündigungsarten (ordentlich / fristlos / fristlos hilfsweise ordentlich)
 * - 10 Fallgruppen
 * - KSchG-Ampel (Grün / Gelb / Rot)
 * - Automatische Fristberechnung nach Zugang
 * - Zusatzoptionen (Freistellung, Rückgabe, Zustellung)
 * - Verbessertes Zustellungsprotokoll
 * - § 38 SGB III + § 623 BGB Hinweis
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import SignaturePad from './ui/SignaturePad';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronRight, ChevronLeft, ChevronDown, AlertTriangle, CheckCircle2,
  Loader2, Scale, FileText, Download, RefreshCw,
  ShieldAlert, ShieldCheck, ShieldQuestion,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Konstanten ────────────────────────────────────────────────────────────────
const KUENDIGUNGSARTEN = [
  {
    value: 'ordentlich',
    label: 'Ordentliche Kündigung',
    desc: 'Unter Einhaltung der gesetzlichen Kündigungsfrist',
    icon: '📋',
  },
  {
    value: 'fristlos',
    label: 'Außerordentliche fristlose Kündigung',
    desc: 'Sofortiger Wirkung gemäß § 626 BGB',
    icon: '⚡',
  },
  {
    value: 'fristlos_hilfsweise',
    label: 'Fristlos, hilfsweise ordentlich',
    desc: 'Fristlos sofort + hilfsweise ordentlich fristgerecht',
    icon: '⚡📋',
  },
];

const FALLGRUPPEN = [
  { value: 'probezeit',           label: 'Probezeitkündigung',                        desc: '2-Wochen-Frist, KSchG gilt nicht' },
  { value: 'kleinbetrieb',        label: 'Kleinbetriebskündigung',                    desc: '≤ 10 Mitarbeiter – KSchG greift nicht' },
  { value: 'nach_probezeit',      label: 'Ordentliche Kündigung nach Probezeit',      desc: 'Gesetzliche Kündigungsfristen § 622 BGB' },
  { value: 'betriebsbedingt',     label: 'Betriebsbedingte Kündigung',                desc: 'Wegfall des Arbeitsplatzes' },
  { value: 'verhaltensbedingt',   label: 'Verhaltensbedingte Kündigung',              desc: 'Pflichtverletzung durch Mitarbeiter' },
  { value: 'personenbedingt',     label: 'Personenbedingte Kündigung / Krankheit',    desc: 'Dauernde Leistungsunfähigkeit' },
  { value: 'unentschuldigt',      label: 'Unentschuldigtes Fehlen',                   desc: 'Fernbleiben ohne Krankmeldung' },
  { value: 'fehlende_krankmeld',  label: 'Fehlende Krankmeldung / Folgebescheinigung',desc: 'Keine Folge-AU eingereicht' },
  { value: 'nach_abmahnung',      label: 'Kündigung nach Abmahnung',                 desc: 'Vorherige schriftliche Abmahnung' },
  { value: 'bg_fall',             label: 'BG/BGHW-Sonderfall',                       desc: 'Berufsgenossenschaft involviert' },
];

const AMPEL_CONFIG = {
  gruen: { color: 'bg-green-100 border-green-400 text-green-800 dark:bg-green-900/20 dark:text-green-300', icon: <ShieldCheck className="h-5 w-5 text-green-600" />, label: 'KSchG greift voraussichtlich NICHT' },
  gelb:  { color: 'bg-amber-100 border-amber-400 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',  icon: <ShieldQuestion className="h-5 w-5 text-amber-600" />,  label: 'Prüfung unvollständig – bitte manuell prüfen' },
  rot:   { color: 'bg-red-100 border-red-400 text-red-800 dark:bg-red-900/20 dark:text-red-300',            icon: <ShieldAlert className="h-5 w-5 text-red-600" />,         label: 'KSchG greift voraussichtlich!' },
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────
export function KuendigungsAssistent({ open, onClose, employee, bogen, locations = [], abmahnungenCount = 0 }) {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [step, setStep] = useState(1);
  const TOTAL_STEPS = 3; // 1: Auswahl, 2: Vorschau+Bestätigen, 3: Fertig

  // Formular-State
  const [art, setArt] = useState('');
  const [fallgruppe, setFallgruppe] = useState('');
  // Geschlecht: aus Personalbogen vorbelegen, aber manuell änderbar
  // Kein Default 'm' – wenn nicht im Personalbogen hinterlegt → leer lassen (Pflichtfeld)
  const _initialGeschlecht = () => {
    const raw = (bogen?.personal?.geschlecht || employee?.geschlecht || '').toLowerCase();
    if (raw.includes('weib') || raw === 'w' || raw === 'frau') return 'w';
    if (raw.includes('männ') || raw === 'm' || raw === 'herr') return 'm';
    return '';  // Unbekannt → Nutzer muss explizit wählen
  };
  const [geschlecht, setGeschlecht] = useState(_initialGeschlecht);
  const [kschgData, setKschgData] = useState(null);
  const [kschgLoading, setKschgLoading] = useState(false);
  const [kschgManuell, setKschgManuell] = useState(false);
  const [kschgManuellCount, setKschgManuellCount] = useState('');
  const [zugangsDatum, setZugangsDatum] = useState(() => new Date().toLocaleDateString('de-DE'));
  const [freistellung, setFreistellung] = useState(false);
  const [freistellungAb, setFreistellungAb] = useState('');
  const [resturlaub, setResturlaub] = useState('');
  const [rueckgabe, setRueckgabe] = useState({
    schluessel: false, transponder: false, tressor: false,
    tankkarte: false, kleidung: false, geraet: false, sonstiges: '',
  });
  const [zustellungArt, setZustellungArt] = useState('persoenlich');
  const [zustellungDatum, setZustellungDatum] = useState('');
  const [zustellungUhrzeit, setZustellungUhrzeit] = useState('');
  const [zustellungOrt, setZustellungOrt] = useState('');
  const [zeugeName, setZeugeName] = useState('');
  const [zeugeFunktion, setZeugeFunktion] = useState('');
  const [annahmeVerweigert, setAnnahmeVerweigert] = useState(false);
  const [boteChecks, setBoteChecks] = useState({ original: false, umschlag: false, adresse: false, einwurf: false });

  // Video-Nachweis Zustellung
  const [videoNachweisAktiv, setVideoNachweisAktiv] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploaded, setVideoUploaded] = useState(false);
  const [videoNotiz, setVideoNotiz] = useState('');
  const videoRef = useRef(null);
  const videoGalleryRef = useRef(null);

  // Phase 1B: Interne Dokumentation
  const [auVon, setAuVon] = useState('');
  const [auBis, setAuBis] = useState('');
  const [folgbescheinigung, setFolgbescheinigung] = useState(null);
  const [auRueckmeldung, setAuRueckmeldung] = useState(null);
  const [ersterFehltag, setErsterFehltag] = useState('');
  const [kontaktversuche, setKontaktversuche] = useState('');
  const [extStelle, setExtStelle] = useState('');
  const [extAnsprechpartner, setExtAnsprechpartner] = useState('');
  const [extDatum, setExtDatum] = useState('');
  const [extUhrzeit, setExtUhrzeit] = useState('');
  const [extArt, setExtArt] = useState('');
  const [extInhalt, setExtInhalt] = useState('');
  const [extRelevanz, setExtRelevanz] = useState('');
  const [externeNurIntern, setExterneNurIntern] = useState(true);
  const [internNotiz, setInternNotiz] = useState('');
  const [kuendGrundIntern, setKuendGrundIntern] = useState('');
  const [auswirkungSchicht, setAuswirkungSchicht] = useState('');
  const [ersatzmassnahmen, setErsatzmassnahmen] = useState('');
  const [anlagenHinweise, setAnlagenHinweise] = useState('');
  const [showAufforderungBtn, setShowAufforderungBtn] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const sigRef = useRef(null);

  // ── Vereinfachter Hauptweg (Aufgabe 2) ────────────────────────────────────
  const [simplifiedChoice, setSimplifiedChoice] = useState(''); // probezeit|normal|fristlos|sonstiger|aufhebung
  const [erweiterteOffen, setErweiterteOffen]   = useState(false);
  const [zustellungExpanded, setZustellungExpanded] = useState(false);
  const [sonstigerGrundText, setSonstigerGrundText] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiText, setAiText] = useState('');

  // Aufhebungsvertrag-spezifische State-Variablen
  const [aufhebBeendigungDatum, setAufhebBeendigungDatum] = useState('');
  const [aufhebAbfindung, setAufhebAbfindung] = useState(false);
  const [aufhebAbfindungBetrag, setAufhebAbfindungBetrag] = useState('');
  const [aufhebFreistellung, setAufhebFreistellung] = useState(false);
  const [aufhebZeugnis, setAufhebZeugnis] = useState(false);

  // Vereinfachte Auswahl → art + fallgruppe automatisch setzen
  const handleSimplifiedChoice = (choice) => {
    setSimplifiedChoice(choice);
    switch (choice) {
      case 'probezeit': setArt('ordentlich'); setFallgruppe('probezeit');       break;
      case 'normal':    setArt('ordentlich'); setFallgruppe('nach_probezeit');  break;
      case 'fristlos':  setArt('fristlos');   setFallgruppe('unentschuldigt'); break;
      case 'sonstiger': setArt('ordentlich'); setFallgruppe('nach_probezeit'); break;
      case 'aufhebung': setArt('aufhebung');  setFallgruppe('');               break;
      default: break;
    }
  };

  // KI-Formulierung für Sonstiger Grund
  const handleGenerateAI = async () => {
    if (!sonstigerGrundText.trim()) { toast.error('Bitte Grund eingeben'); return; }
    setAiGenerating(true);
    try {
      const r = await axios.post(`${API}/api/kuendigung/assistent/generate-text`, {
        grund: sonstigerGrundText,
        art: art || 'ordentlich',
      }, { headers });
      setAiText(r.data.text || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'KI-Formulierung fehlgeschlagen');
    } finally { setAiGenerating(false); }
  };

  const [fristData, setFristData] = useState(null);
  // Manuelle Beschäftigungsdaten
  const [beschManuell, setBeschManuell] = useState(false);
  const [manuellesEintrDatum, setManuellesEintrDatum] = useState('');
  const [manProbezeitStatus, setManProbezeitStatus] = useState('unbekannt');
  const [manProbezeitDauer, setManProbezeitDauer] = useState('6');
  const [manProbezeitEnde, setManProbezeitEnde] = useState('');
  const [manFristBekannt, setManFristBekannt] = useState(false);
  const [manFristText, setManFristText] = useState('');
  const [beschGrund, setBeschGrund] = useState('');
  const [fristLoading, setFristLoading] = useState(false);
  const [manuellesDatum, setManuellesDatum] = useState('');
  const [manuellerGrund, setManuellerGrund] = useState('');
  const [fristManuell, setFristManuell] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedDoc, setSavedDoc] = useState(null);
  const [downloading, setDownloading] = useState('');

  // Auto-Daten aus Personalbogen
  const ag = bogen?.arbeitgeber || {};
  const pe = bogen?.personal || {};
  const firma = locations.find(l => l.id === ag.location_id) || locations[0] || {};

  const maName = employee?.name || `${pe.vorname || ''} ${pe.familienname || ''}`.trim() || 'Mitarbeiter';
  const eintrittsdatum = ag.eintrittsdatum ? new Date(ag.eintrittsdatum).toLocaleDateString('de-DE') : '';
  const locationId = ag.location_id || employee?.location_ids?.[0];

  // ── Reset beim Öffnen ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setStep(1); setArt(''); setFallgruppe(''); setKschgData(null);
    setGeschlecht(_initialGeschlecht());
    setKschgManuell(false); setKschgManuellCount('');
    setZugangsDatum(new Date().toLocaleDateString('de-DE'));
    setFreistellung(false); setFreistellungAb(''); setResturlaub('');
    setRueckgabe({ schluessel: false, transponder: false, tressor: false, tankkarte: false, kleidung: false, geraet: false, sonstiges: '' });
    setZustellungArt('persoenlich'); setZustellungDatum(''); setZustellungUhrzeit('');
    setZustellungOrt(''); setZeugeName(''); setZeugeFunktion('');
    setAnnahmeVerweigert(false); setSigEmpty(true); setSavedDoc(null);
    setFristData(null); setBoteChecks({ original: false, umschlag: false, adresse: false, einwurf: false });
    setManuellesDatum(''); setManuellerGrund(''); setFristManuell(false);
    setBeschManuell(false); setManuellesEintrDatum(''); setManProbezeitStatus('unbekannt');
    setManProbezeitDauer('6'); setManProbezeitEnde(''); setManFristBekannt(false);
    setManFristText(''); setBeschGrund('');
    setVideoNachweisAktiv(false); setVideoUploading(false); setVideoUploaded(false);
    setVideoNotiz('');
    setAuVon(''); setAuBis(''); setFolgbescheinigung(null); setAuRueckmeldung(null);
    setErsterFehltag(''); setKontaktversuche(''); setExtStelle(''); setExtAnsprechpartner('');
    setExtDatum(''); setExtUhrzeit(''); setExtArt(''); setExtInhalt(''); setExtRelevanz('');
    setExterneNurIntern(true); setInternNotiz(''); setKuendGrundIntern(''); setAuswirkungSchicht('');
    setErsatzmassnahmen(''); setAnlagenHinweise(''); setShowAufforderungBtn(false);
    // Vereinfachter Hauptweg zurücksetzen
    setSimplifiedChoice(''); setErweiterteOffen(false); setZustellungExpanded(false);
    setSonstigerGrundText(''); setAiGenerating(false); setAiText('');
  }, [open]);

  // ── KSchG-Berechnung ───────────────────────────────────────────────────────
  const fetchKschg = useCallback(async () => {
    if (!employee?.id) return;
    setKschgLoading(true);
    try {
      const params = new URLSearchParams({ employee_id: employee.id });
      if (locationId) params.append('location_id', locationId);
      const res = await axios.get(`${API}/api/kuendigung/kschg-check?${params}`, { headers });
      setKschgData(res.data);
      if (!kschgManuell) setKschgManuellCount(String(res.data.mitarbeiter_count ?? ''));
    } catch { setKschgData({ ampel: 'gelb', ampel_text: 'Berechnung nicht möglich', mitarbeiter_count: null }); }
    finally { setKschgLoading(false); }
  }, [employee?.id, locationId]);

  useEffect(() => { if (step === 2 && !kschgData) fetchKschg(); }, [step]);
  // Fristberechnung läuft automatisch über das useEffect unten (step === 2)

  // ── Fristberechnung ────────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== 2 || !eintrittsdatum) return;  // Step 2 = Vorschau/Bestätigen
    setFristLoading(true);
    const artFür = art === 'ordentlich' ? 'ordentlich' : 'fristlos';
    // Effektives Eintrittsdatum: manuell hat Vorrang über Personalbogen
    const effEintrDatum = beschManuell && manuellesEintrDatum ? manuellesEintrDatum : eintrittsdatum;
    const probezeitMonate = beschManuell
      ? parseInt(manProbezeitDauer) || 6
      : parseInt(ag.probezeit_dauer) || 6;
    const probezeitAktiv = beschManuell
      ? manProbezeitStatus === 'ja'
      : (fallgruppe === 'probezeit' || !!ag.probezeit);

    if (!effEintrDatum) { setFristData(null); setFristLoading(false); return; }

    axios.get(`${API}/api/kuendigung/fristberechnung`, {
      params: {
        eintrittsdatum: effEintrDatum,
        kuendigungsart: artFür,
        zugangs_datum: zugangsDatum,
        probezeit: probezeitAktiv,
        probezeit_dauer_monate: probezeitMonate,
      },
      headers
    }).then(r => { setFristData(r.data); setManuellesDatum(''); })
      .catch(() => setFristData(null))
      .finally(() => setFristLoading(false));
  }, [step, art, fallgruppe, eintrittsdatum]);

  // ── Speichern + PDFs ───────────────────────────────────────────────────────
  const handleVideoUpload = async (file) => {
    if (!savedDoc?.id) { toast.error('Bitte erst Kündigung speichern, dann Video hochladen'); return; }
    setVideoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (videoNotiz) formData.append('notiz', videoNotiz);
      await axios.post(`${API}/api/kuendigung/assistent/${savedDoc.id}/video`, formData, { headers });
      setVideoUploaded(true);
      toast.success('Video-Nachweis gespeichert ✅');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Video-Upload fehlgeschlagen');
    } finally { setVideoUploading(false); }
  };

  const handleSave = async () => {
    // ── Aufhebungsvertrag: eigener Speicherpfad ─────────────────────────────
    if (simplifiedChoice === 'aufhebung') {
      if (!aufhebBeendigungDatum.trim()) { toast.error('Bitte Beendigungsdatum eingeben'); return; }
      setSaving(true);
      try {
        const res = await axios.post(`${API}/api/kuendigung/aufhebungsvertrag`, {
          employee_id: employee?.id || '',
          mitarbeiter_name: maName,
          mitarbeiter_strasse: pe.strasse ? `${pe.strasse} ${pe.hausnummer || ''}`.trim() : '',
          mitarbeiter_plz_ort: pe.plz ? `${pe.plz} ${pe.ort || ''}`.trim() : '',
          geschlecht,
          eintrittsdatum,
          location_id: locationId,
          firma_name: firma.legal_name || firma.firma || firma.name || ag.firma || '',
          firma_adresse: firma.legal_address || firma.adresse || ag.firma_adresse || '',
          firma_plz_ort: firma.legal_plz_ort || firma.plz_ort || firma.city || ag.firma_plz_ort || '',
          firma_unterzeichner: firma.legal_unterzeichner || firma.legal_owner || ag.firma_unterzeichner || '',
          standort: firma.name || ag.standort || '',
          beendigungs_datum: aufhebBeendigungDatum,
          abfindung: aufhebAbfindung,
          abfindung_betrag: aufhebAbfindung && aufhebAbfindungBetrag ? parseFloat(aufhebAbfindungBetrag) : null,
          freistellung: aufhebFreistellung,
          zeugnis_klausel: aufhebZeugnis,
        }, { headers });
        setSavedDoc(res.data);
        setStep(3);
        toast.success('Aufhebungsvertrag gespeichert');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
      } finally { setSaving(false); }
      return;
    }
    // ── Kündigung: bestehender Speicherpfad ────────────────────────────────
    try {
      const sigData = (!sigEmpty && sigRef.current && !sigRef.current.isEmpty())
        ? sigRef.current.toDataURL('image/png') : null;

      const count = kschgManuell ? parseFloat(kschgManuellCount) : kschgData?.mitarbeiter_count;
      const payload = {
        employee_id: employee?.id || '',
        mitarbeiter_name: maName,
        mitarbeiter_strasse: pe.strasse ? `${pe.strasse} ${pe.hausnummer || ''}`.trim() : '',
        mitarbeiter_plz_ort: pe.plz ? `${pe.plz} ${pe.ort || ''}`.trim() : '',
        geschlecht: geschlecht,
        eintrittsdatum,
        location_id: locationId,
        beschaeftigung_art: ag.arbeitszeit || '',
        firma_name: firma.legal_name || firma.firma || firma.name || ag.firma || '',
        firma_adresse: firma.legal_address || firma.adresse || ag.firma_adresse || '',
        firma_plz_ort: firma.legal_plz_ort || firma.plz_ort || firma.city || ag.firma_plz_ort || '',
        firma_unterzeichner: firma.legal_unterzeichner || firma.legal_owner || ag.firma_unterzeichner || '',
        standort: firma.name || ag.standort || '',
        kuendigungsart_assistent: art,
        fallgruppe,
        kuendigungs_datum: fristManuell && manuellesDatum ? manuellesDatum : (fristData?.kuendigungs_datum || ''),
        frist_manuell: fristManuell,
        frist_manuell_grund: fristManuell ? manuellerGrund : null,
        // Manuelle Beschäftigungsdaten
        besch_daten_quelle: beschManuell ? 'manuell' : 'personalbogen',
        manuelles_eintrittsdatum: beschManuell ? manuellesEintrDatum : null,
        manuelle_probezeit: beschManuell ? manProbezeitStatus : null,
        manuelle_probezeit_dauer: beschManuell ? manProbezeitDauer : null,
        manuelle_probezeit_ende: beschManuell ? manProbezeitEnde : null,
        manuelle_kuendigungsfrist: (beschManuell && manFristBekannt) ? manFristText : null,
        manuelle_frist_begruendung: beschManuell ? beschGrund : null,
        zugangs_datum: zugangsDatum,
        probezeit_aktiv: fallgruppe === 'probezeit',
        probezeit_dauer: ag.probezeit_dauer || '3 Monate',
        kschg_ampel: kschgData?.ampel || 'gelb',
        kschg_mitarbeiter_count: count || null,
        kschg_mitarbeiter_count_manuell: kschgManuell,
        kschg_beschaeftigungsdauer_monate: kschgData?.beschaeftigungsdauer_monate || null,
        freistellung,
        freistellung_ab: freistellungAb,
        resturlaub_tage: resturlaub ? parseInt(resturlaub) : null,
        rueckgabe_schluessel: rueckgabe.schluessel,
        rueckgabe_transponder: rueckgabe.transponder,
        rueckgabe_tressorschluessel: rueckgabe.tressor,
        rueckgabe_tankkarte: rueckgabe.tankkarte,
        rueckgabe_arbeitskleidung: rueckgabe.kleidung,
        rueckgabe_geraet: rueckgabe.geraet,
        rueckgabe_sonstiges: rueckgabe.sonstiges || null,
        zustellung_art: zustellungArt,
        zustellung_datum: zustellungDatum,
        zustellung_uhrzeit: zustellungUhrzeit,
        zustellung_ort: zustellungOrt,
        zeuge_name: zeugeName,
        zeuge_funktion: zeugeFunktion,
        zeuge_signature: sigData,
        annahme_verweigert: annahmeVerweigert,
        bote_original_gesehen: boteChecks.original,
        bote_umschlag_gelegt: boteChecks.umschlag,
        bote_adresse_geprueft: boteChecks.adresse,
        bote_einwurf_bestaetigt: boteChecks.einwurf,
        // Phase 1B: Interne Dokumentation
        au_von: auVon || null,
        au_bis: auBis || null,
        folgbescheinigung_erhalten: folgbescheinigung,
        au_rueckmeldung_erhalten: auRueckmeldung,
        erster_fehltag: ersterFehltag || null,
        kontaktversuche: kontaktversuche || null,
        ext_stelle: extStelle || null,
        ext_ansprechpartner: extAnsprechpartner || null,
        ext_datum: extDatum || null,
        ext_uhrzeit: extUhrzeit || null,
        ext_art: extArt || null,
        ext_inhalt: extInhalt || null,
        ext_relevanz: extRelevanz || null,
        ext_nur_intern: externeNurIntern,
        interne_notiz: internNotiz || null,
        kuendigungsgrund_freitext: aiText.trim() || kuendGrundIntern || null,  // KI-Text hat Vorrang
        auswirkung_schicht: auswirkungSchicht || null,
        ersatzmassnahmen: ersatzmassnahmen || null,
        anlagen_hinweise: anlagenHinweise || null,
      };

      const res = await axios.post(`${API}/api/kuendigung/assistent`, payload, { headers });
      setSavedDoc(res.data);
      setStep(3);
      toast.success('Kündigung gespeichert');
      // Aufforderung zur Rückmeldung anzeigen wenn AU fehlt
      setShowAufforderungBtn(!folgbescheinigung || !auRueckmeldung);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const downloadPDF = async (type) => {
    if (!savedDoc?.id) return;
    setDownloading(type);
    try {
      const urlMap = {
        schreiben:        `${API}/api/kuendigung/assistent/${savedDoc.id}/pdf`,
        protokoll:        `${API}/api/kuendigung/assistent/${savedDoc.id}/pdf-protokoll`,
        intern:           `${API}/api/kuendigung/assistent/${savedDoc.id}/pdf-intern`,
        aufforderung:     `${API}/api/kuendigung/assistent/${savedDoc.id}/pdf-aufforderung`,
        aufhebungsvertrag:`${API}/api/kuendigung/aufhebungsvertrag/${savedDoc.id}/pdf`,
      };
      const nameMap = {
        schreiben:        `Kuendigung_${maName.replace(/ /g, '_')}.pdf`,
        protokoll:        `Zustellprotokoll_${maName.replace(/ /g, '_')}.pdf`,
        intern:           `Intern_Doku_${maName.replace(/ /g, '_')}.pdf`,
        aufforderung:     `Aufforderung_${maName.replace(/ /g, '_')}.pdf`,
        aufhebungsvertrag:`Aufhebungsvertrag_${maName.replace(/ /g, '_')}.pdf`,
      };
      const res = await axios.get(urlMap[type], { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = nameMap[type];
      a.click();
    } catch { toast.error('PDF-Download fehlgeschlagen'); }
    finally { setDownloading(''); }
  };

  // ── UI Helpers ─────────────────────────────────────────────────────────────
  const ampelCfg = AMPEL_CONFIG[kschgData?.ampel || 'gelb'];

  const canNext = () => {
    if (step === 1) return !!simplifiedChoice && !!geschlecht && (simplifiedChoice !== 'aufhebung' || true);
    if (step === 2) return true;
    return false;
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Scale className="h-5 w-5 text-primary" />
            Kündigungsassistent – {maName}
          </DialogTitle>
          {/* Schritt-Anzeige */}
          {step < 5 && (
            <div className="flex gap-1 mt-2">
              {['Kündigungsart', 'Fallgruppe & KSchG', 'Frist & Optionen', 'Zustellung', ''].slice(0, 4).map((label, i) => (
                <div key={i} className={`flex-1 h-1.5 rounded-full transition-colors ${i + 1 <= step ? 'bg-primary' : 'bg-muted'}`} />
              ))}
            </div>
          )}
        </DialogHeader>

        {/* § 623 BGB Hinweis */}
        {step < 6 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 dark:text-amber-300 flex gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              <strong>§ 623 BGB – Schriftformerfordernis:</strong> Eine Kündigung ist nur in Schriftform wirksam.
              Das Dokument muss ausgedruckt und eigenhändig unterschrieben werden.
              Kündigung per WhatsApp, E-Mail oder digitaler Signatur ist rechtlich unwirksam.
            </span>
          </div>
        )}

        <div className="space-y-4 py-1">

          {/* ── SCHRITT 1: Vereinfachte Auswahl ─────────────────────────── */}
          {step === 1 && (
            <div className="space-y-3">
              <div className="bg-muted/40 rounded-xl p-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Kündigung für</p>
                  <p className="font-semibold text-sm">{maName}</p>
                  {eintrittsdatum && <p className="text-xs text-muted-foreground">Eintritt: {eintrittsdatum}</p>}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <p className="text-xs text-muted-foreground">Anrede <span className="text-destructive">*</span></p>
                  <div className="flex gap-1">
                    {[{v:'m',l:'Herr'},{v:'w',l:'Frau'}].map(({v,l}) => (
                      <button key={v} type="button" onClick={() => setGeschlecht(v)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all ${
                          geschlecht === v ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40'
                        }`} data-testid={`geschlecht-${v}`}>{l}</button>
                    ))}
                  </div>
                  {!geschlecht && <p className="text-xs text-destructive">Bitte wählen</p>}
                </div>
              </div>

              <p className="text-sm font-semibold">Warum wird gekündigt?</p>
              {[
                { value: 'probezeit', icon: '📋', label: 'Probezeitkündigung',    desc: '2-Wochen-Frist, kein KSchG' },
                { value: 'normal',   icon: '📄', label: 'Ordentliche Kündigung', desc: 'Gesetzliche Frist nach § 622 BGB' },
                { value: 'fristlos', icon: '⚡', label: 'Fristlose Kündigung',   desc: 'Sofort, schwerwiegender Grund (§ 626 BGB)' },
                { value: 'sonstiger',icon: '✏️', label: 'Sonstiger Grund',       desc: 'Du beschreibst kurz – KI formuliert professionell' },
                { value: 'aufhebung',icon: '🤝', label: 'Aufhebungsvertrag',     desc: 'Einvernehmliche Auflösung – freies Beendigungsdatum' },
              ].map(opt => (
                <button key={opt.value} type="button" onClick={() => handleSimplifiedChoice(opt.value)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3 ${
                    simplifiedChoice === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                  }`}>
                  <span className="text-xl mt-0.5 shrink-0">{opt.icon}</span>
                  <div className="flex-1">
                    <p className={`font-semibold text-sm ${simplifiedChoice === opt.value ? 'text-primary' : 'text-foreground'}`}>{opt.label}</p>
                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                  </div>
                  {simplifiedChoice === opt.value && <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />}
                </button>
              ))}

              {/* Erweiterte Optionen */}
              <div className="border border-border rounded-xl overflow-hidden">
                <button type="button" onClick={() => setErweiterteOffen(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-muted-foreground hover:bg-muted/50 transition-colors">
                  <span className="font-medium">Erweiterte Optionen <span className="font-normal">(KSchG, Fallgruppen, Interne Dokumentation)</span></span>
                  {erweiterteOffen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                {erweiterteOffen && (
                  <div className="border-t border-border p-4 space-y-5">
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Kündigungsgrund / Fallgruppe (detailliert)</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {FALLGRUPPEN.map(f => (
                          <button key={f.value} type="button" onClick={() => setFallgruppe(f.value)}
                            className={`text-left p-3 rounded-xl border-2 transition-all ${
                              fallgruppe === f.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                            }`}>
                            <p className="font-medium text-sm">{f.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{f.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2 border-t pt-4">
                      <p className="text-sm font-semibold flex items-center gap-2"><Scale className="h-4 w-4" /> KSchG-Prüfung (§ 23 KSchG)</p>
                      {kschgLoading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 border rounded-xl">
                          <Loader2 className="h-4 w-4 animate-spin" /> Berechne...
                        </div>
                      ) : kschgData ? (
                        <div className={`border-2 rounded-xl p-3 space-y-2 ${AMPEL_CONFIG[kschgData.ampel || 'gelb'].color}`}>
                          <div className="flex items-center gap-2 font-semibold text-sm">
                            {AMPEL_CONFIG[kschgData.ampel || 'gelb'].icon} {AMPEL_CONFIG[kschgData.ampel || 'gelb'].label}
                          </div>
                          <p className="text-xs">{kschgData.ampel_text}</p>
                          <div className="flex items-center gap-2 text-xs">
                            <span>MA-Zahl: <strong>{kschgData.mitarbeiter_count ?? '?'}</strong></span>
                            <button onClick={fetchKschg} className="ml-auto underline text-xs flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Neu</button>
                          </div>
                          <label className="flex items-center gap-2 text-xs cursor-pointer border-t border-current/20 pt-1">
                            <input type="checkbox" checked={kschgManuell} onChange={e => setKschgManuell(e.target.checked)} className="rounded" />
                            Manuell überschreiben
                          </label>
                          {kschgManuell && <Input type="number" step="0.5" min="0" value={kschgManuellCount} onChange={e => setKschgManuellCount(e.target.value)} placeholder="Gewichtete MA-Zahl" className="h-8 text-sm w-44" />}
                        </div>
                      ) : (
                        <button onClick={fetchKschg} className="text-xs text-primary underline">KSchG-Ampel berechnen</button>
                      )}
                    </div>
                    <div className="space-y-2 border-t pt-4">
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="checkbox" checked={beschManuell} onChange={e => { setBeschManuell(e.target.checked); setFristData(null); }} className="rounded" />
                        <span className="font-medium">Beschäftigungsdaten manuell eingeben</span>
                      </label>
                      {beschManuell && (
                        <div className="pl-4 space-y-3 border-l-2 border-blue-300">
                          <div className="space-y-1"><Label className="text-xs font-semibold">Eintrittsdatum *</Label>
                            <Input value={manuellesEintrDatum} onChange={e => setManuellesEintrDatum(e.target.value)} placeholder="TT.MM.JJJJ" className="h-8 text-sm w-40" onBlur={() => setFristData(null)} /></div>
                          <div className="space-y-1.5"><Label className="text-xs font-semibold">Probezeit vereinbart?</Label>
                            <div className="flex gap-2">
                              {[['ja','Ja'],['nein','Nein'],['unbekannt','Unbekannt']].map(([v,l]) => (
                                <button key={v} type="button" onClick={() => { setManProbezeitStatus(v); setFristData(null); }}
                                  className={`px-3 py-1.5 text-xs rounded-xl border-2 transition-all ${manProbezeitStatus === v ? 'border-primary bg-primary/5 font-semibold' : 'border-border'}`}>{l}</button>
                              ))}
                            </div>
                          </div>
                          {manProbezeitStatus === 'ja' && (
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1"><Label className="text-xs">Probezeitdauer</Label>
                                <select value={manProbezeitDauer} onChange={e => { setManProbezeitDauer(e.target.value); setFristData(null); }} className="h-8 w-full text-sm border rounded-lg px-2 bg-background">
                                  <option value="3">3 Monate</option><option value="6">6 Monate</option>
                                </select></div>
                              <div className="space-y-1"><Label className="text-xs">Probezeit-Ende</Label>
                                <Input value={manProbezeitEnde} onChange={e => setManProbezeitEnde(e.target.value)} placeholder="TT.MM.JJJJ" className="h-8 text-sm" /></div>
                            </div>
                          )}
                          <div className="space-y-1"><Label className="text-xs">Begründung</Label>
                            <Input value={beschGrund} onChange={e => setBeschGrund(e.target.value)} placeholder="z.B. Eintrittsdatum aus Arbeitsvertrag" className="h-8 text-sm" /></div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3 border-t pt-4">
                      <p className="text-xs text-muted-foreground">Interne Angaben – erscheinen nur im internen PDF, nicht im Kündigungsschreiben.</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1"><Label className="text-xs">AU gültig von</Label><Input value={auVon} onChange={e => setAuVon(e.target.value)} placeholder="TT.MM.JJJJ" className="h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs">AU gültig bis</Label><Input value={auBis} onChange={e => setAuBis(e.target.value)} placeholder="TT.MM.JJJJ" className="h-8 text-sm" /></div>
                      </div>
                      <div className="space-y-1"><Label className="text-xs">Kontaktversuche</Label>
                        <textarea value={kontaktversuche} onChange={e => setKontaktversuche(e.target.value)} rows={2} className="w-full text-sm border rounded-xl px-3 py-2 bg-background focus:outline-none resize-none" /></div>
                      <div className="space-y-1"><Label className="text-xs">Interne Notiz</Label>
                        <textarea value={internNotiz} onChange={e => setInternNotiz(e.target.value)} rows={2} className="w-full text-sm border rounded-xl px-3 py-2 bg-background focus:outline-none resize-none" /></div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1"><Label className="text-xs">Auswirkung Schichtplanung</Label><Input value={auswirkungSchicht} onChange={e => setAuswirkungSchicht(e.target.value)} className="h-8 text-sm" /></div>
                        <div className="space-y-1"><Label className="text-xs">Ersatzmaßnahmen</Label><Input value={ersatzmassnahmen} onChange={e => setErsatzmassnahmen(e.target.value)} className="h-8 text-sm" /></div>
                        <div className="space-y-1 col-span-2"><Label className="text-xs">Anlagen / Hinweise</Label><Input value={anlagenHinweise} onChange={e => setAnlagenHinweise(e.target.value)} placeholder="z.B. 2 Abmahnungen" className="h-8 text-sm" /></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── SCHRITT 2: Aufhebungsvertrag-Formular ─────────────────── */}
          {step === 2 && simplifiedChoice === 'aufhebung' && (
            <div className="space-y-5">
              {/* Zusammenfassung */}
              <div className="bg-muted/40 rounded-xl p-4 space-y-1">
                <p className="text-sm font-semibold">Aufhebungsvertrag prüfen</p>
                <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                  <div><p className="text-xs text-muted-foreground">Mitarbeiter</p><p className="font-medium">{maName}</p></div>
                  <div><p className="text-xs text-muted-foreground">Eingetreten am</p><p className="font-medium">{eintrittsdatum || '–'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Firma</p><p className="font-medium text-xs">{firma.legal_name || firma.firma || firma.name || '–'}</p></div>
                </div>
              </div>

              {/* Beendigungsdatum (Pflicht) */}
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">Beendigungsdatum <span className="text-destructive">*</span></Label>
                <p className="text-xs text-muted-foreground">Frei vereinbartes Datum – kein gesetzliches Fristschema</p>
                <Input
                  type="text"
                  value={aufhebBeendigungDatum}
                  onChange={e => setAufhebBeendigungDatum(e.target.value)}
                  placeholder="TT.MM.JJJJ"
                  className="h-10 w-44"
                  data-testid="aufheb-beendigung-datum"
                />
              </div>

              {/* Abfindung */}
              <div className="space-y-2 border rounded-xl p-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                  <input type="checkbox" checked={aufhebAbfindung} onChange={e => setAufhebAbfindung(e.target.checked)}
                    className="rounded" data-testid="aufheb-abfindung-check" />
                  Abfindung vereinbaren
                </label>
                <p className="text-xs text-muted-foreground">Optional – falls beide Parteien eine Abfindung vereinbaren</p>
                {aufhebAbfindung && (
                  <div className="pt-1">
                    <Label className="text-xs">Abfindungsbetrag (Brutto in EUR)</Label>
                    <Input
                      type="number" min="0" step="100"
                      value={aufhebAbfindungBetrag}
                      onChange={e => setAufhebAbfindungBetrag(e.target.value)}
                      placeholder="z.B. 3000"
                      className="h-9 w-44 mt-1"
                      data-testid="aufheb-abfindung-betrag"
                    />
                  </div>
                )}
              </div>

              {/* Freistellung */}
              <div className="border rounded-xl p-4 space-y-1">
                <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                  <input type="checkbox" checked={aufhebFreistellung} onChange={e => setAufhebFreistellung(e.target.checked)}
                    className="rounded" data-testid="aufheb-freistellung-check" />
                  Freistellung bis Beendigungsdatum
                </label>
                <p className="text-xs text-muted-foreground">
                  Mitarbeiter wird ab sofort von der Arbeit freigestellt (unter Fortzahlung der Vergütung).
                  Standard (nicht aktiviert): Mitarbeiter arbeitet normal bis zum letzten Tag.
                </p>
              </div>

              {/* Zeugnis-Klausel */}
              <div className="border rounded-xl p-4 space-y-1">
                <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                  <input type="checkbox" checked={aufhebZeugnis} onChange={e => setAufhebZeugnis(e.target.checked)}
                    className="rounded" data-testid="aufheb-zeugnis-check" />
                  Anspruch auf qualifiziertes Zeugnis im Vertrag festhalten
                </label>
                <p className="text-xs text-muted-foreground">
                  Formulierung: "Beendigung im gegenseitigen Einvernehmen" (§ arbeitszeugnis.py)
                </p>
              </div>

              {/* Speichern */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl p-3 text-xs text-blue-800 dark:text-blue-300">
                <strong>Hinweis:</strong> Aufhebungsverträge bedürfen ebenfalls der Schriftform (§ 623 BGB).
                Bitte ausdrucken und von beiden Parteien eigenhändig unterschreiben.
              </div>

              <Button onClick={handleSave} disabled={saving || !aufhebBeendigungDatum.trim()} className="w-full gap-2 mt-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                Aufhebungsvertrag erstellen &amp; speichern
              </Button>
            </div>
          )}

          {/* ── SCHRITT 2: Vorschau + Datum + Bestätigen ─────────────────── */}
          {step === 2 && simplifiedChoice !== 'aufhebung' && (
            <div className="space-y-4">
              <div className="bg-muted/40 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold">Kündigung prüfen</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><p className="text-xs text-muted-foreground">Mitarbeiter</p><p className="font-medium">{maName}</p></div>
                  <div>
                    <p className="text-xs text-muted-foreground">Anrede</p>
                    <div className="flex gap-1 mt-0.5">
                      {[{v:'m',l:'Herr'},{v:'w',l:'Frau'}].map(({v,l}) => (
                        <button key={v} type="button" onClick={() => setGeschlecht(v)}
                          className={`px-2 py-0.5 text-xs font-semibold rounded border-2 transition-all ${
                            geschlecht === v ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'
                          }`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div><p className="text-xs text-muted-foreground">Kündigungsart</p>
                    <p className="font-medium">{simplifiedChoice === 'probezeit' ? 'Probezeitkündigung' : simplifiedChoice === 'normal' ? 'Ordentliche Kündigung' : simplifiedChoice === 'fristlos' ? 'Fristlose Kündigung' : 'Sonstiger Grund (ordentlich)'}</p>
                  </div>
                  <div><p className="text-xs text-muted-foreground">Eintrittsdatum</p><p className="font-medium">{eintrittsdatum || '–'}</p></div>
                </div>

                {/* Kündigungsdatum – prominent, editierbar */}
                <div className="pt-3 border-t border-border/50 space-y-2">
                  <p className="text-xs font-semibold text-foreground">Kündigungsdatum / letzter Arbeitstag *</p>
                  {fristLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Berechne Frist nach § 622 BGB…</div>
                  ) : fristData && !fristManuell ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <span className="text-base font-bold text-green-700 dark:text-green-400">{fristData.kuendigungs_datum}</span>
                        <span className="text-xs text-muted-foreground">({fristData.kuendigungsfrist} · § 622 BGB)</span>
                      </div>
                      <button type="button" onClick={() => { setFristManuell(true); setManuellesDatum(fristData.kuendigungs_datum || ''); }}
                        className="text-xs text-primary underline">Abweichendes Datum wählen</button>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <Input type="text" value={manuellesDatum} onChange={e => { setManuellesDatum(e.target.value); setFristManuell(true); }}
                        placeholder="TT.MM.JJJJ" className="h-9 text-sm w-44" />
                      {!fristData && !eintrittsdatum && <p className="text-xs text-amber-700">Eintrittsdatum fehlt – Frist kann nicht auto-berechnet werden.</p>}
                      {fristManuell && fristData && (
                        <button type="button" onClick={() => { setFristManuell(false); setManuellesDatum(''); }}
                          className="text-xs text-primary underline">Zurück zur berechneten Frist ({fristData.kuendigungs_datum})</button>
                      )}
                      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Grund (optional)</Label>
                        <Input value={manuellerGrund} onChange={e => setManuellerGrund(e.target.value)} placeholder="z.B. vertragliche Vereinbarung" className="h-8 text-sm" /></div>
                    </div>
                  )}
                </div>

                {/* Freistellung */}
                <div className="pt-2 border-t border-border/50">
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={freistellung} onChange={e => setFreistellung(e.target.checked)} className="rounded" />
                    Mit unwiderruflicher Freistellung
                  </label>
                  {freistellung && (
                    <div className="grid grid-cols-2 gap-2 mt-2 pl-5">
                      <div className="space-y-1"><Label className="text-xs">Freigestellt ab</Label><Input value={freistellungAb} onChange={e => setFreistellungAb(e.target.value)} placeholder="TT.MM.JJJJ" className="h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs">Resturlaub (Tage)</Label><Input type="number" value={resturlaub} onChange={e => setResturlaub(e.target.value)} placeholder="0" className="h-8 text-sm" /></div>
                    </div>
                  )}
                </div>

                {/* Rückgabe */}
                <div className="pt-2 border-t border-border/50">
                  <p className="text-sm font-medium mb-2">Rückgabe Firmeneigentum (optional)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                    {[['schluessel','Schlüssel'],['transponder','Transponder'],['tressor','Tressorschlüssel'],['tankkarte','Tankkarte'],['kleidung','Arbeitskleidung'],['geraet','Gerät']].map(([key,label]) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="checkbox" checked={rueckgabe[key]} onChange={e => setRueckgabe(r => ({...r,[key]:e.target.checked}))} className="rounded" />{label}
                      </label>
                    ))}
                  </div>
                  <Input value={rueckgabe.sonstiges} onChange={e => setRueckgabe(r => ({...r,sonstiges:e.target.value}))} placeholder="Sonstiges…" className="h-8 text-sm mt-2" />
                </div>
              </div>

              {/* KI-Formulierung (nur Sonstiger Grund) */}
              {simplifiedChoice === 'sonstiger' && (
                <div className="space-y-3 border border-primary/30 rounded-xl p-4">
                  <p className="text-sm font-semibold">✨ Formulierungshilfe</p>
                  <p className="text-xs text-muted-foreground">Beschreibe kurz den Kündigungsgrund in eigenen Worten – nur für dich, nicht der Wortlaut im Schreiben.</p>
                  <textarea value={sonstigerGrundText} onChange={e => setSonstigerGrundText(e.target.value)} rows={3}
                    placeholder="z.B. Mitarbeiterin erscheint regelmäßig zu spät und hat mehrere Ermahnungen ignoriert…"
                    className="w-full rounded-xl border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary" />
                  <Button type="button" size="sm" variant="outline" onClick={handleGenerateAI}
                    disabled={aiGenerating || !sonstigerGrundText.trim()} className="gap-2">
                    {aiGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : '✨'}
                    KI-Formulierung erstellen
                  </Button>
                  {aiText && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Generierter Text – bitte prüfen, dann anpassen:</p>
                      <textarea value={aiText} onChange={e => setAiText(e.target.value)} rows={4}
                        className="w-full rounded-xl border-2 border-primary/30 bg-primary/5 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary" />
                      <p className="text-xs text-muted-foreground">Wird als Zusatzabsatz im Kündigungsschreiben verwendet.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Optionale Zustellung (eingeklappt) */}
              <div className="border border-border rounded-xl overflow-hidden">
                <button type="button" onClick={() => setZustellungExpanded(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-muted-foreground hover:bg-muted/50 transition-colors">
                  <span className="font-medium">Zustellungsprotokoll ausfüllen <span className="font-normal">(optional – für rechtssicheren Nachweis empfohlen)</span></span>
                  {zustellungExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                {zustellungExpanded && (
                  <div className="border-t border-border p-4 space-y-4">
                    <div className="flex gap-3">
                      {[['persoenlich','Persönlich übergeben'],['bote','Per Boten']].map(([val,lbl]) => (
                        <button key={val} type="button" onClick={() => setZustellungArt(val)}
                          className={`flex-1 py-2.5 text-sm rounded-xl border-2 font-medium transition-all ${zustellungArt === val ? 'border-primary bg-primary/5 text-primary' : 'border-border'}`}>{lbl}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1"><Label className="text-xs">Datum *</Label><Input value={zustellungDatum} onChange={e => setZustellungDatum(e.target.value)} placeholder="TT.MM.JJJJ" className="h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs">Uhrzeit</Label><Input value={zustellungUhrzeit} onChange={e => setZustellungUhrzeit(e.target.value)} placeholder="HH:MM" className="h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs">Ort</Label><Input value={zustellungOrt} onChange={e => setZustellungOrt(e.target.value)} placeholder="Standort" className="h-8 text-sm" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1"><Label className="text-xs">Name Zeuge / Bote {zustellungArt === 'bote' ? '*' : '(optional)'}</Label>
                        <Input value={zeugeName} onChange={e => setZeugeName(e.target.value)} placeholder="Vollständiger Name" className="h-8 text-sm" /></div>
                      <div className="space-y-1"><Label className="text-xs">Funktion</Label>
                        <Input value={zeugeFunktion} onChange={e => setZeugeFunktion(e.target.value)} placeholder="z.B. Stationsleitung" className="h-8 text-sm" /></div>
                    </div>
                    {zustellungArt === 'bote' && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-muted-foreground">Bote bestätigt:</p>
                        {[['original','Original gesehen'],['umschlag','In adressierten Umschlag'],['adresse','Adresse geprüft'],
                          ['einwurf',`Einwurf ${zustellungDatum || '__'} ${zustellungUhrzeit || '__'} Uhr`]].map(([key,label]) => (
                          <label key={key} className="flex items-start gap-2 cursor-pointer text-xs">
                            <input type="checkbox" checked={boteChecks[key]} onChange={e => setBoteChecks(b => ({...b,[key]:e.target.checked}))} className="rounded mt-0.5" />{label}
                          </label>
                        ))}
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Unterschrift Zeuge / Bote</Label>
                      <div className={`border-2 rounded-xl overflow-hidden bg-white dark:bg-gray-900 ${sigEmpty ? 'border-border' : 'border-green-400'}`}>
                        <SignaturePad ref={sigRef} penColor="#1E40AF" height={130} onEnd={() => setSigEmpty(false)} />
                      </div>
                      <button type="button" onClick={() => { sigRef.current?.clear(); setSigEmpty(true); }} className="text-xs text-muted-foreground underline">Zurücksetzen</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Speichern */}
              <Button onClick={handleSave} disabled={saving} className="w-full gap-2 mt-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                Kündigung erstellen &amp; speichern
              </Button>
            </div>
          )}

          {/* ── SCHRITT 3: Fertig + Downloads ─────────────────────────────── */}
          {step === 3 && savedDoc && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl p-4">
                <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
                <div>
                  <p className="font-semibold text-green-800 dark:text-green-300">
                    {simplifiedChoice === 'aufhebung' ? 'Aufhebungsvertrag gespeichert!' : 'Kündigung gespeichert!'}
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-400">
                    {maName} · {simplifiedChoice === 'aufhebung'
                      ? `Aufhebungsvertrag · Beendigung: ${savedDoc.beendigungs_datum || savedDoc.kuendigungs_datum || '–'}`
                      : `${simplifiedChoice === 'fristlos' ? 'Fristlose Kündigung' : 'Kündigung'} · Fristende: ${savedDoc.kuendigungs_datum || '–'}`}
                  </p>
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 rounded-xl p-4 text-sm text-red-800 dark:text-red-300">
                <p className="font-bold mb-1">Bitte sofort ausdrucken und unterschreiben!</p>
                <p className="text-xs">§ 623 BGB: Nur in <strong>Schriftform</strong> mit eigenhändiger Unterschrift beider Parteien wirksam.</p>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {simplifiedChoice === 'aufhebung' ? (
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground font-medium">Für beide Parteien (je eine Ausfertigung):</p>
                    <Button onClick={() => downloadPDF('aufhebungsvertrag')} disabled={downloading === 'aufhebungsvertrag'} className="gap-2 w-full">
                      {downloading === 'aufhebungsvertrag' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Aufhebungsvertrag PDF
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-0.5">
                      <p className="text-xs text-muted-foreground font-medium">Für Mitarbeiter:</p>
                      <Button onClick={() => downloadPDF('schreiben')} disabled={downloading === 'schreiben'} className="gap-2 w-full">
                        {downloading === 'schreiben' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Kündigungsschreiben PDF
                      </Button>
                    </div>
                    {savedDoc.zustellung_art === 'bote' && (
                      <div className="space-y-0.5">
                        <p className="text-xs text-muted-foreground font-medium">Zugangsnachweis:</p>
                        <Button onClick={() => downloadPDF('protokoll')} disabled={downloading === 'protokoll'} variant="outline" className="gap-2 w-full">
                          {downloading === 'protokoll' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          Zustellungsprotokoll PDF
                        </Button>
                      </div>
                    )}
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium text-red-700">Nur für Personalakte (NICHT an Mitarbeiter!):</p>
                      <Button onClick={() => downloadPDF('intern')} disabled={downloading === 'intern'} variant="outline" className="gap-2 w-full border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20">
                        {downloading === 'intern' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Interne Dokumentation PDF
                      </Button>
                    </div>
                  </>
                )}
              </div>
              <div className="text-xs text-muted-foreground border-t pt-3">
                {simplifiedChoice === 'aufhebung'
                  ? <p><strong>Hinweis:</strong> Beide Parteien sollten je eine unterschriebene Ausfertigung erhalten. Bitte auch bei der Bundesagentur mögliche Sperrzeit-Auswirkungen klären.</p>
                  : <p><strong>§ 38 SGB III:</strong> Mitarbeiter muss sich innerhalb von 3 Tagen arbeitssuchend melden.</p>
                }
              </div>
            </div>
          )}

        </div>

        {/* Footer-Navigation */}
        {step < 3 && (
          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              {step === 1 ? 'Abbrechen' : 'Zurück'}
            </Button>
            {step === 1 && (
              <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()}>
                Weiter <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        )}
        {step === 3 && (
          <div className="flex justify-end pt-2 border-t">
            <Button variant="outline" onClick={onClose}>Schließen</Button>
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
}
